// 画面の判断に使う純関数（役割・案件・帳票）。テスト対象。
import type { AgentInvokeResult, CaseState, DocStatus, Me, WorkCase } from './types';

// ---- 役割 ----

/** その事業所の利用者を登録できるか（事業所管理者か、同じ法人の法人担当者） */
export function canManageOffice(me: Me | null, officeId: string | null): boolean {
  if (!me || !officeId) return false;
  const office = me.principal.offices.find((o) => o.officeId === officeId);
  return me.principal.roles.some(
    (r) => (r.scopeKind === 'office' && r.role === 'office_admin' && r.officeId === officeId) || (r.scopeKind === 'corp' && r.role === 'corp_admin' && !!office && r.corpId === office.corpId),
  );
}

export function isOperator(me: Me | null): boolean {
  return !!me?.principal.roles.some((r) => r.scopeKind === 'ops' && r.role === 'operator');
}

/** 保存済みの選択が所属に無ければ捨てる。所属が 1 つならそれを選ぶ */
export function pickOffice(me: Me, stored: string | null): string | null {
  const ids = me.principal.offices.map((o) => o.officeId);
  if (stored && ids.includes(stored)) return stored;
  return ids.length === 1 ? ids[0]! : null;
}

// ---- 案件 ----

export function questionText(c: Pick<WorkCase, 'question'>): string {
  const q = c.question?.q;
  return typeof q === 'string' ? q : '';
}

export interface AgentOutcome {
  status: 'waiting' | 'done' | 'not-claimed' | 'not-runnable' | 'error' | 'not-accepted' | 'unknown';
  generation?: number;
  question?: string;
  text?: string;
}

/** API の agent 欄を読む。構造のまま来る result を優先し、無ければ detail（応答 JSON の先頭 1000 文字）を解く */
export function parseAgentOutcome(r: AgentInvokeResult | undefined | null): AgentOutcome {
  if (!r) return { status: 'unknown' };
  if (!r.accepted) return { status: 'not-accepted' };
  try {
    const j = (r.result ?? JSON.parse(r.detail ?? '')) as Record<string, unknown>;
    const s = j.status;
    if (s === 'waiting' || s === 'done' || s === 'not-claimed' || s === 'not-runnable' || s === 'error') {
      return {
        status: s,
        generation: typeof j.generation === 'number' ? j.generation : undefined,
        question: typeof j.question === 'string' ? j.question : undefined,
        text: typeof j.text === 'string' ? j.text : undefined,
      };
    }
  } catch {
    // 切れた JSON などは状態不明として、一覧の取り直しで確かめる
  }
  return { status: 'unknown' };
}

export function outcomeLabel(o: AgentOutcome): string {
  switch (o.status) {
    case 'waiting':
      return '判断を求められています';
    case 'done':
      return '完了しました';
    case 'error':
      return 'エージェントの処理に失敗しました（あとで再処理されます）';
    case 'not-claimed':
    case 'not-runnable':
      return 'すでに別の処理が動いています';
    case 'not-accepted':
      return 'エージェントに渡せませんでした。時間をおいて画面を読み込み直してください';
    default:
      return '受け付けました。結果は一覧で確かめてください';
  }
}

export const CASE_STATE_LABEL: Record<CaseState, string> = {
  todo: '未着手',
  running: '実行中',
  waiting: '判断待ち',
  retry: '再処理待ち',
  suspended: '中断',
  done: '完了',
};

export const CASE_KIND_LABEL: Record<string, string> = {
  draft_support_progress: '支援経過の下書き',
  basis_corrected: '根拠の訂正',
  import: '取り込み',
};

/** 最近更新された順に n 件 */
export function recent<T extends { updatedAt: string }>(items: T[], n: number): T[] {
  return [...items].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)).slice(0, n);
}

// ---- 帳票 ----

export const DOC_STATUS_LABEL: Record<DocStatus, string> = {
  draft: '下書き',
  review: '確認中',
  confirmed: '確定済',
  revised: '改訂済',
};

export function canReview(s: DocStatus): boolean {
  return s === 'draft';
}

export function canConfirm(s: DocStatus): boolean {
  return s === 'draft' || s === 'review';
}

/** docType ごとにまとめ、様式の並び順で並べる（未登録の様式は後ろ）。各群は版の新しい順 */
export function groupByDocType<T extends { docType: string; version: number }>(docs: T[], order: readonly string[]): Array<{ docType: string; items: T[] }> {
  const m = new Map<string, T[]>();
  for (const d of docs) m.set(d.docType, [...(m.get(d.docType) ?? []), d]);
  const rank = (t: string) => (order.includes(t) ? order.indexOf(t) : order.length);
  return [...m.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([docType, items]) => ({ docType, items: items.sort((a, b) => b.version - a.version) }));
}

/** 利用者の名前の絞り込み（名前・かなの部分一致、空白は無視） */
export function matchClient(c: { name: string; kana: string | null }, q: string): boolean {
  const norm = (s: string) => s.replace(/[\s　]+/g, '').toLowerCase();
  const k = norm(q);
  if (!k) return true;
  return norm(c.name).includes(k) || norm(c.kana ?? '').includes(k);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
